import React, { useEffect, useRef, useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Checkbox,
  FormControlLabel,
} from "@mui/material"
import MenuIcon from "@mui/icons-material/Menu"
import HomeIcon from "@mui/icons-material/Home"
import RefreshIcon from "@mui/icons-material/Refresh"
import LogoutIcon from "@mui/icons-material/Logout"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import LocalHospitalIcon from "@mui/icons-material/LocalHospital"
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined"
import AssignmentIcon from "@mui/icons-material/Assignment"
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined"
import { useTheme } from "@mui/material/styles"
import { useDispatch, useSelector } from "react-redux"
import type { AppDispatch } from "@core/store"
import { memberSelector } from "@/features/member/memberSelectors"
import { logout } from "@/features/member/memberSlice"
import { resetSettings } from "@/features/hospitalSetting/hospitalSettingSlice"
import { logoutMemberSession } from "@core/api/memberApi"
import { useDemoMode } from "@core/contexts/DemoModeContext"
import { DEFAULT_TREATMENT_BED_COUNT } from "@core/api/hospitalFavoriteApi"
import { useDeviceMode } from "@/modules/android/hooks/useDeviceMode"
import { HeaderSize } from "@/modules/android/styles/AndroidResponsive"
import { useAppVersion } from "@/modules/android/hooks/useAppVersion"
import AndroidLayerDialog from "@modules/android/components/AndroidLayerDialog"

const ANDROID_TOP_INSET = "max(env(safe-area-inset-top, 0px), 24px)"
const LOCAL_BED_COUNT_KEY = "android_treatment_room_bed_count"
const BED_COUNT_CHANGED_EVENT = "android-treatment-room-bed-count-changed"
const LOCAL_NAME_MASKING_KEY = "android_treatment_room_name_masking"
const NAME_MASKING_CHANGED_EVENT = "android-treatment-room-name-masking-changed"
const PRESET_BED_COUNTS = Array.from({ length: 20 }, (_, index) => index + 1)
const HEADER_SCALE = 0.8
const REFRESH_ICON_SCALE = 1.1

const readBedCount = () => {
  try {
    const stored = window.localStorage.getItem(LOCAL_BED_COUNT_KEY)
    const parsed = Number(stored)
    if (Number.isInteger(parsed) && parsed > 0) {
      return parsed
    }
  } catch {
    // noop
  }
  return DEFAULT_TREATMENT_BED_COUNT
}

const readNameMaskingEnabled = () => {
  try {
    const stored = window.localStorage.getItem(LOCAL_NAME_MASKING_KEY)
    if (stored === null) {
      return true
    }
    return stored !== "false"
  } catch {
    return true
  }
}

const AndroidMainTitleBar: React.FC = () => {
  const theme = useTheme()
  const { mode, isPhone } = useDeviceMode()
  const header = HeaderSize[mode]
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const location = useLocation()
  const member = useSelector(memberSelector)
  const { isDemoMode, isNoChartMode, demoMemberName, exitDemoMode } = useDemoMode()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [configuredBedCount, setConfiguredBedCount] = useState<number>(readBedCount)
  const [nameMaskingEnabled, setNameMaskingEnabled] = useState<boolean>(readNameMaskingEnabled)
  const [isEditingBedCount, setIsEditingBedCount] = useState(false)
  const [bedCountInput, setBedCountInput] = useState(String(readBedCount()))
  const [bedCountError, setBedCountError] = useState("")
  const [softwareInfoOpen, setSoftwareInfoOpen] = useState(false)
  const versionLabel = useAppVersion()
  const appBarRef = useRef<HTMLElement | null>(null)
  const isLoggedIn = isDemoMode || !!member?.memberUuid
  const profileName = isDemoMode ? demoMemberName : member?.memberProfileName || member?.memberName || ""
  const scaledTabFontSize = `calc(${header.tabFontSize} * ${HEADER_SCALE})`
  const scaledButtonFontSize = `calc(${header.buttonFontSize} * ${HEADER_SCALE})`
  const scaledHeaderFontSize = `calc(${header.fontSize} * ${HEADER_SCALE})`
  const scaledHeaderIconSize = header.iconSize * HEADER_SCALE
  const scaledAvatarSize = 24 * HEADER_SCALE

  // 현재 활성 탭 판별
  const currentPath = location.pathname
  const isOnTreatmentRoom = currentPath.startsWith("/android/treatment-room") || currentPath === "/android"
  const isOnPatients = currentPath.startsWith("/android/patients")
  const isOnConsent = currentPath.startsWith("/android/consent") || currentPath.startsWith("/android/no-chart-consent")
  const isOnNoChartConsent = currentPath.startsWith("/android/no-chart-consent")
  const isOnReception = currentPath.startsWith("/android/reception")

  const open = Boolean(anchorEl)

  useEffect(() => {
    setConfiguredBedCount(readBedCount())
    setNameMaskingEnabled(readNameMaskingEnabled())
  }, [isOnTreatmentRoom])

  useEffect(() => {
    const handleBedCountChanged = (event: Event) => {
      const customEvent = event as CustomEvent<number>
      const nextCount = Number(customEvent.detail)
      if (!Number.isInteger(nextCount) || nextCount <= 0) {
        return
      }

      setConfiguredBedCount(nextCount)

      if (!isEditingBedCount) {
        setBedCountInput(String(nextCount))
        setBedCountError("")
      }
    }

    window.addEventListener(BED_COUNT_CHANGED_EVENT, handleBedCountChanged)
    return () => {
      window.removeEventListener(BED_COUNT_CHANGED_EVENT, handleBedCountChanged)
    }
  }, [isEditingBedCount])

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(appBarRef.current || event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleServerSearch = () => {
    handleMenuClose()
    navigate("/server-search")
  }

  const handleGoHome = () => {
    handleMenuClose()
    navigate("/android")
  }

  const handleReload = () => {
    handleMenuClose()
    window.location.reload()
  }

  const handleGoTreatmentRoom = () => {
    window.dispatchEvent(new Event("android-treatment-room-reset"))
    navigate("/android/treatment-room")
  }

  const handleOpenBedCountDialog = () => {
    setBedCountInput(String(configuredBedCount))
    setBedCountError("")
    setIsEditingBedCount(true)
  }

  const handleCloseBedCountDialog = () => {
    setIsEditingBedCount(false)
    setBedCountError("")
  }

  const saveBedCount = (nextCount: number) => {
    setConfiguredBedCount(nextCount)
    window.localStorage.setItem(LOCAL_BED_COUNT_KEY, String(nextCount))
    window.dispatchEvent(new CustomEvent(BED_COUNT_CHANGED_EVENT, { detail: nextCount }))
  }

  const saveNameMaskingEnabled = (enabled: boolean) => {
    setNameMaskingEnabled(enabled)
    window.localStorage.setItem(LOCAL_NAME_MASKING_KEY, enabled ? "true" : "false")
    window.dispatchEvent(new CustomEvent(NAME_MASKING_CHANGED_EVENT, { detail: enabled }))
  }

  const handleSelectPresetBedCount = (nextCount: number) => {
    saveBedCount(nextCount)
    setBedCountInput(String(nextCount))
    setIsEditingBedCount(false)
    setBedCountError("")
  }

  const handleSaveBedCount = () => {
    const nextCount = Number(bedCountInput.trim())
    if (!Number.isInteger(nextCount) || nextCount <= 0) {
      setBedCountError("1 이상의 정수를 입력하세요.")
      return
    }
    saveBedCount(nextCount)
    setIsEditingBedCount(false)
    setBedCountError("")
  }

  const handleOpenSoftwareInfo = () => {
    handleMenuClose()
    setSoftwareInfoOpen(true)
  }

  const handleLogout = () => {
    handleMenuClose()
    if (isDemoMode) {
      exitDemoMode()
      navigate("/android/login", { replace: true })
      return
    }

    void (async () => {
      await logoutMemberSession()
      dispatch(logout())
      dispatch(resetSettings())
      navigate("/android/login", { replace: true })
    })()
  }

  return (
    <AppBar
      ref={node => {
        appBarRef.current = node
      }}
      position="fixed"
      elevation={1}
      sx={{
        height: `calc(${header.height}px + ${ANDROID_TOP_INSET})`,
        minHeight: `calc(${header.height}px + ${ANDROID_TOP_INSET})`,
        paddingTop: ANDROID_TOP_INSET,
        justifyContent: "center",
        backgroundColor: theme.palette.grey[900],
        color: theme.palette.common.white,
        borderBottom: "1px solid",
        borderColor: theme.palette.grey[800],
        zIndex: theme.zIndex.appBar,
      }}
    >
      <Toolbar sx={{ minHeight: `${header.height}px !important`, px: header.toolbarPx, gap: header.toolbarGap }}>
        <IconButton size="small" edge="start" color="inherit" onClick={handleMenuOpen} sx={{ mr: 1 }}>
          <MenuIcon sx={{ fontSize: scaledHeaderIconSize }} />
        </IconButton>

        {/* 로그인 시 탭 메뉴: 치료실 / 동의서 또는 연동없이 동의서 단일 탭 */}
        {isLoggedIn && (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {isNoChartMode ? (
              <Button
                size="small"
                startIcon={<DescriptionOutlinedIcon sx={{ fontSize: scaledHeaderIconSize }} />}
                onClick={() => {
                  window.dispatchEvent(new Event("android-nochart-consent-reset"))
                  navigate("/android/no-chart-consent")
                }}
                sx={{
                  color: isOnNoChartConsent ? theme.palette.common.white : theme.palette.grey[400],
                  fontWeight: isOnNoChartConsent ? 700 : 400,
                  fontSize: scaledTabFontSize,
                  borderBottom: isOnNoChartConsent ? `2px solid ${theme.palette.common.white}` : "2px solid transparent",
                  borderRadius: 0,
                  px: 1,
                  minWidth: 0,
                  textTransform: "none",
                }}
              >
                연동없이 동의서 받기
              </Button>
            ) : (
              <>
                <Button
                  size="small"
                  startIcon={<AssignmentIcon sx={{ fontSize: scaledHeaderIconSize }} />}
                  onClick={() => navigate("/android/reception/settings")}
                  sx={{
                    color: isOnReception ? theme.palette.common.white : theme.palette.grey[400],
                    fontWeight: isOnReception ? 700 : 400,
                    fontSize: scaledTabFontSize,
                    borderBottom: isOnReception ? `2px solid ${theme.palette.common.white}` : "2px solid transparent",
                    borderRadius: 0,
                    px: 1,
                    minWidth: 0,
                    textTransform: "none",
                  }}
                >
                  접수하기
                </Button>
                <Button
                  size="small"
                  startIcon={<PeopleAltOutlinedIcon sx={{ fontSize: scaledHeaderIconSize }} />}
                  onClick={() => navigate("/android/patients")}
                  sx={{
                    color: isOnPatients ? theme.palette.common.white : theme.palette.grey[400],
                    fontWeight: isOnPatients ? 700 : 400,
                    fontSize: scaledTabFontSize,
                    borderBottom: isOnPatients ? `2px solid ${theme.palette.common.white}` : "2px solid transparent",
                    borderRadius: 0,
                    px: 1,
                    minWidth: 0,
                    textTransform: "none",
                  }}
                >
                  환자보기
                </Button>
                <Button
                  size="small"
                  startIcon={<LocalHospitalIcon sx={{ fontSize: scaledHeaderIconSize }} />}
                  onClick={handleGoTreatmentRoom}
                  sx={{
                    color: isOnTreatmentRoom ? theme.palette.common.white : theme.palette.grey[400],
                    fontWeight: isOnTreatmentRoom ? 700 : 400,
                    fontSize: scaledTabFontSize,
                    borderBottom: isOnTreatmentRoom ? `2px solid ${theme.palette.common.white}` : "2px solid transparent",
                    borderRadius: 0,
                    px: 1,
                    minWidth: 0,
                    textTransform: "none",
                  }}
                >
                  치료실
                </Button>
                <Button
                  size="small"
                  startIcon={<DescriptionOutlinedIcon sx={{ fontSize: scaledHeaderIconSize }} />}
                  onClick={() => navigate("/android/consent")}
                  sx={{
                    color: isOnConsent ? theme.palette.common.white : theme.palette.grey[400],
                    fontWeight: isOnConsent ? 700 : 400,
                    fontSize: scaledTabFontSize,
                    borderBottom: isOnConsent ? `2px solid ${theme.palette.common.white}` : "2px solid transparent",
                    borderRadius: 0,
                    px: 1,
                    minWidth: 0,
                    textTransform: "none",
                  }}
                >
                  동의서
                </Button>
              </>
            )}
          </Box>
        )}

        <Box sx={{ flex: 1 }} />

        {isLoggedIn && isOnConsent && (
          <Box sx={{ display: "flex", alignItems: "center", mr: 1.5 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => window.dispatchEvent(new Event("android-consent-pin-reset"))}
              sx={{
                color: theme.palette.common.white,
                borderColor: theme.palette.common.white,
                fontSize: scaledButtonFontSize,
                "&:hover": {
                  borderColor: theme.palette.common.white,
                  backgroundColor: "rgba(255, 255, 255, 0.08)",
                },
              }}
            >
              PIN 설정
            </Button>
          </Box>
        )}

        {isLoggedIn && isOnTreatmentRoom && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: 1.5 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleOpenBedCountDialog}
              sx={{
                fontSize: scaledButtonFontSize,
                color: theme.palette.common.white,
                borderColor: theme.palette.common.white,
                "&:hover": {
                  borderColor: theme.palette.common.white,
                  backgroundColor: "rgba(255, 255, 255, 0.08)",
                },
              }}
            >
              치료실 설정
            </Button>
          </Box>
        )}

        {isLoggedIn && (
          <IconButton
            size="small"
            color="inherit"
            onClick={handleReload}
            sx={{
              mr: 0.5,
              color: theme.palette.common.white,
            }}
          >
            <RefreshIcon sx={{ fontSize: header.iconSize * REFRESH_ICON_SCALE }} />
          </IconButton>
        )}

        {isLoggedIn && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
            <Avatar alt={profileName} sx={{ width: scaledAvatarSize, height: scaledAvatarSize }} />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                fontSize: scaledHeaderFontSize,
                maxWidth: isPhone ? 80 : 120,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {profileName}
            </Typography>
          </Box>
        )}
      </Toolbar>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0,
            },
          },
        }}
      >
        <MenuItem onClick={handleGoHome}>
          <ListItemIcon>
            <HomeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>처음화면</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleOpenSoftwareInfo}>
          <ListItemIcon>
            <InfoOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>소프트웨어정보</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>로그아웃</ListItemText>
        </MenuItem>
      </Menu>

      <AndroidLayerDialog open={softwareInfoOpen} onClose={() => setSoftwareInfoOpen(false)} title="소프트웨어정보">
        <Box sx={{ textAlign: "center", py: 2 }}>
          <Typography variant="body1" fontWeight={600} gutterBottom>
            나인티나인클라우드(주)
          </Typography>
          <Typography variant="body1" gutterBottom>
            시그마차트 링크
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {versionLabel || "개발 모드"}
          </Typography>
        </Box>
      </AndroidLayerDialog>

      <Dialog open={isEditingBedCount} onClose={handleCloseBedCountDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>치료실 설정</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
              이름 마스킹하기
            </Typography>
            <FormControlLabel
              control={<Checkbox checked={nameMaskingEnabled} onChange={(_, checked) => saveNameMaskingEnabled(checked)} />}
              label="이름 마스킹하기"
              sx={{ ml: 0 }}
            />
          </Box>

          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
              베드 갯수
            </Typography>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 1, mt: 1, mb: 2 }}>
            {PRESET_BED_COUNTS.map(count => (
              <Button
                key={count}
                size="small"
                variant={configuredBedCount === count ? "contained" : "outlined"}
                onClick={() => handleSelectPresetBedCount(count)}
              >
                {count}
              </Button>
            ))}
          </Box>

          <TextField
            autoFocus
            fullWidth
            size="small"
            type="number"
            label="직접입력"
            value={bedCountInput}
            onChange={e => {
              setBedCountInput(e.target.value)
              if (bedCountError) setBedCountError("")
            }}
            error={!!bedCountError}
            helperText={bedCountError || "1 이상의 정수만 입력 가능합니다."}
            inputProps={{ min: 1, step: 1 }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={handleCloseBedCountDialog}>
            취소
          </Button>
          <Button variant="contained" onClick={handleSaveBedCount}>
            저장
          </Button>
        </DialogActions>
      </Dialog>
    </AppBar>
  )
}

export default AndroidMainTitleBar

